import apiClient from '@/shared/api/client';

// ── Types ─────────────────────────────────────────────────────────────────────

export type SafetyRole =
  | 'provider'
  | 'hygienist'
  | 'assistant'
  | 'sterilization_tech'
  | 'clinical_team_manager'
  | 'front_desk'
  | 'office_manager';

export type AssignmentStatus = 'complete' | 'pending' | 'due_soon' | 'overdue';

export type EquipmentStatus = 'ok' | 'due_soon' | 'overdue' | 'action_required' | 'event_driven';

export type PnAction = 'Deliver' | 'Track' | 'Refer';

export interface LogField {
  key: string;
  label: string;
  kind: 'boolean' | 'text' | 'select';
  options?: string[];
  passField?: boolean;
}

export interface SafetyContent {
  course: {
    id: string;
    title: string;
    version: string;
    cadence: string;
    passThreshold: number;
  };
  roles: { value: SafetyRole; label: string }[];
  topics: { id: string; title: string; shortTitle: string; citation: string; pnAction: PnAction }[];
  equipmentPack: {
    packId: string;
    name: string;
    logDefId: string;
    cadenceLabel: string;
    defaultQty: number;
    qtyAdjustable: boolean;
    note?: string;
  }[];
  logDefinitions: {
    id: string;
    name: string;
    cadence: string;
    pnAction: PnAction;
    citation: string;
    fields: LogField[];
    escalationCopy: string;
  }[];
  caOverlay: OverlayRow[];
  occasionalItems: {
    id: string;
    title: string;
    authority: string;
    pnAction: PnAction;
    detail: string;
    disclaimer: string;
  }[];
  copy: {
    attestation: string;
    noCeNote: string;
    sporeEscalation: string;
  };
}

export interface OverlayRow {
  requirement: string;
  source: string;
  pnAction: PnAction;
  citation: string;
  lastVerified: string;
}

export interface StaffAssignment {
  id: string;
  kind: string;
  cycleYear: number;
  dueDate: string;
  topics: string[];
  status: AssignmentStatus;
  completedAt: string | null;
  quizScore: number | null;
  signed: boolean;
}

export interface StaffMember {
  id: string;
  name: string;
  roles: SafetyRole[];
  roleLabel: string;
  bbpExposed: boolean;
  active: boolean;
  assignment: StaffAssignment | null;
}

export interface EquipmentItem {
  id: string;
  type: string;
  label: string;
  name: string;
  cadence: string;
  pnAction: PnAction;
  citation: string;
  fields: LogField[];
  status: EquipmentStatus;
  escalationState: string | null;
  escalationLabel: string | null;
  escalationCopy: string;
  lastLoggedAt: string | null;
  lastResult: string | null;
  nextDue: string | null;
}

export interface AttentionItem {
  severity: 'overdue' | 'due_soon';
  title: string;
  detail: string;
  pnAction: PnAction;
  href: string;
}

export interface SafetyOverview {
  onboarded: boolean;
  config: { trainingMonth: number; launchState: string } | null;
  rollup: { complete: number; total: number; pct: number };
  nextCycleLabel: string;
  attention: AttentionItem[];
  staff: StaffMember[];
  equipment: EquipmentItem[];
  ecp: { reviewedAt: string | null; nextReview: string | null } | null;
  recordCount: number;
  attestation: string;
}

export interface CourseTopic {
  id: string;
  title: string;
  shortTitle: string;
  source: string;
  citation: string;
  pnAction: PnAction;
  sections: { heading: string; body: string }[];
}

export interface CourseQuizItem {
  id: string;
  topicId: string;
  q: string;
  options: string[];
}

export interface CoursePayload {
  course: { id: string; title: string; version: string };
  staff: { id: string; name: string; roles: SafetyRole[]; roleLabel: string };
  assignment: { id: string; kind: string; dueDate: string; completed: boolean };
  topics: CourseTopic[];
  quiz: CourseQuizItem[];
  passThreshold: number;
  ackText: string;
  attestation: string;
  noCeNote: string;
}

export interface QuizAnswer {
  itemId: string;
  answerIndex: number;
}

export interface QuizResult {
  correct: number;
  total: number;
  score: number;
  passed: boolean;
}

export interface CompletionRecord {
  id: string;
  staffName: string;
  roleLabel: string;
  courseTitle: string;
  courseVersion: string;
  kind: string;
  topics: string[];
  completedAt: string;
  quizScore: number;
  passed: boolean;
  signed: boolean;
  signerName: string | null;
  signedAt: string | null;
  ackVersion: string | null;
}

export interface EcpSection {
  heading: string;
  body: string;
}

export interface RecordsPayload {
  completions: CompletionRecord[];
  ecp: {
    version: number;
    reviewedAt: string | null;
    nextReview: string | null;
    content: { sections: EcpSection[] };
  } | null;
  caOverlay: OverlayRow[];
  attestation: string;
}

export interface LogEntry {
  id: string;
  equipmentId: string;
  logDefId: string;
  loggedAt: string;
  values: Record<string, string | boolean>;
  result: 'pass' | 'fail';
  escalation: string | null;
  equipment: { id: string; label: string | null; type: string };
}

export interface SetupInput {
  locationId: string;
  trainingMonth: number;
  staff: { name: string; roles: SafetyRole[]; bbpExposed?: boolean }[];
  equipment: { packId: string; qty: number }[];
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

// ── API functions ─────────────────────────────────────────────────────────────

export const safetyApi = {
  getContent: (): Promise<SafetyContent> =>
    apiClient.get<Envelope<SafetyContent>>('/safety/content').then((r) => r.data.data),

  getOverview: (locationId: string): Promise<SafetyOverview> =>
    apiClient
      .get<Envelope<SafetyOverview>>('/safety/overview', { params: { locationId } })
      .then((r) => r.data.data),

  getRoster: (locationId: string): Promise<{ name: string; roles: SafetyRole[] | null }[]> =>
    apiClient
      .get<Envelope<{ name: string; roles: SafetyRole[] | null }[]>>('/safety/roster', {
        params: { locationId },
      })
      .then((r) => r.data.data),

  setup: (input: SetupInput): Promise<SafetyOverview> =>
    apiClient.post<Envelope<SafetyOverview>>('/safety/setup', input).then((r) => r.data.data),

  skipSetup: (locationId: string): Promise<void> =>
    apiClient.post('/safety/setup/skip', { locationId }).then(() => undefined),

  listStaff: (locationId: string): Promise<StaffMember[]> =>
    apiClient
      .get<Envelope<StaffMember[]>>('/safety/staff', { params: { locationId } })
      .then((r) => r.data.data),

  addStaff: (input: {
    locationId: string;
    name: string;
    roles: SafetyRole[];
    bbpExposed?: boolean;
  }): Promise<StaffMember> =>
    apiClient.post<Envelope<StaffMember>>('/safety/staff', input).then((r) => r.data.data),

  updateStaff: (
    id: string,
    locationId: string,
    patch: { roles?: SafetyRole[]; bbpExposed?: boolean; active?: boolean },
  ): Promise<StaffMember> =>
    apiClient
      .patch<Envelope<StaffMember>>(`/safety/staff/${id}`, patch, { params: { locationId } })
      .then((r) => r.data.data),

  getCourse: (assignmentId: string, locationId: string): Promise<CoursePayload> =>
    apiClient
      .get<Envelope<CoursePayload>>(`/safety/assignments/${assignmentId}/course`, {
        params: { locationId },
      })
      .then((r) => r.data.data),

  submitQuiz: (
    assignmentId: string,
    locationId: string,
    answers: QuizAnswer[],
  ): Promise<QuizResult> =>
    apiClient
      .post<
        Envelope<QuizResult>
      >(`/safety/assignments/${assignmentId}/quiz`, { answers }, { params: { locationId } })
      .then((r) => r.data.data),

  complete: (
    assignmentId: string,
    locationId: string,
    input: { answers: QuizAnswer[]; signerName: string },
  ): Promise<{ result: QuizResult }> =>
    apiClient
      .post<
        Envelope<{ result: QuizResult }>
      >(`/safety/assignments/${assignmentId}/complete`, { ...input, ackConfirmed: true }, { params: { locationId } })
      .then((r) => r.data.data),

  listEquipment: (locationId: string): Promise<EquipmentItem[]> =>
    apiClient
      .get<Envelope<EquipmentItem[]>>('/safety/equipment', { params: { locationId } })
      .then((r) => r.data.data),

  addEquipment: (input: { locationId: string; packId: string; qty: number }): Promise<void> =>
    apiClient.post('/safety/equipment', input).then(() => undefined),

  removeEquipment: (id: string, locationId: string): Promise<void> =>
    apiClient.delete(`/safety/equipment/${id}`, { params: { locationId } }).then(() => undefined),

  createLogEntry: (
    equipmentId: string,
    locationId: string,
    values: Record<string, string | boolean>,
  ): Promise<{
    result: 'pass' | 'fail';
    escalationState: string | null;
    escalationCopy: string | null;
  }> =>
    apiClient
      .post<
        Envelope<{
          result: 'pass' | 'fail';
          escalationState: string | null;
          escalationCopy: string | null;
        }>
      >(`/safety/equipment/${equipmentId}/logs`, { values }, { params: { locationId } })
      .then((r) => r.data.data),

  listLogs: (locationId: string, equipmentId?: string): Promise<LogEntry[]> =>
    apiClient
      .get<Envelope<LogEntry[]>>('/safety/logs', { params: { locationId, equipmentId } })
      .then((r) => r.data.data),

  getRecords: (locationId: string): Promise<RecordsPayload> =>
    apiClient
      .get<Envelope<RecordsPayload>>('/safety/records', { params: { locationId } })
      .then((r) => r.data.data),

  reviewEcp: (locationId: string): Promise<void> =>
    apiClient.post('/safety/ecp/review', { locationId }).then(() => undefined),
};
